import axios from 'axios'
import api from '@/api/auth'
import * as types from '../mutation-types'
import decode from 'jwt-decode'
// initial state
// shape: [{ id, quantity }]
const state = {
  user: {
    uid: null,
    name: 'unknown',
    email: '-',
    level: 0,
    isLogin: Boolean(localStorage.getItem('_t')),
    serviceList: [],
    mobileServiceList: []
  }
}

// getters
const getters = {
  user: state => state.user,
  isPaidMember: state => {
    /**
     * 유료, 무료 회원을 구분하는 플래그입니다.
     * 로그인한 사용자 정보에서 포인트, 만료기간을 확인합니다.
     * 포인트가 0보다 클 경우, 만료기간이 남았을 경우 유료회원으로 구분합니다.
     */

    const user = state.user
    const hasPoint = user.point > 0
    const hasExpirationDate = user.expiration_date !== ''

    return hasPoint && hasExpirationDate
  }
}

// actions
const actions = {
  // 로그인한 사용자가 현재 페이지에 접근가능한지를 확인한다.
  checkAccessMenu () {
    return new Promise((resolve, reject) => {
      /**
       * 페이지에 접근한 사용자의 권한을 확인한다.
       * 사용자 등급별 이용 할 수 있는 메뉴리스트에 경를 확인한다.
       * 사용자가 해당 페이지에 경로를 가지고 있는지를 확인해 권한여부를 결정한다.
       */
      if (state.user.hasOwnProperty('serviceList')) {
        const route = window.Vue.$route
        const path = route.path
        const serviceList = state.user.serviceList
        let cnt = 0

        if (serviceList) {
          for (let i = 0; i < serviceList.length; i++) {
            if (serviceList[i].path !== '' && _.includes(path, serviceList[i].path)) {
              cnt++
            }
          }
        }

        if (cnt > 0) {
          return resolve()
        } else {
          // 고객문의 페이지로 이동
          window.Vue.$router.push({name: 'contact'})
          alert('접근 권한이 없습니다.')
          return reject(new Error())
        }
      }
    })
  },

  // 로그인한 사용자에 정보를 불러온다.
  getUserInfo ({ commit }, data) {
    return new Promise((resolve, reject) => {
      api.getUserInfo(data, response => {
        // error 발생 시
        if (response.error) {
          reject(new Error(response.detail))
        } else {
          const user = response.result
          commit(types.USER_INFO, { user })
          resolve(response)
        }
      })
    })
  },

  // 로그인한 사용자에 과금정보를 불러온다. (포인트, 사용만료기간)
  getPricingInfo ({ commit }) {
    return new Promise((resolve, reject) => {
      api.getPricingInfo(response => {
        // error 발생 시
        if (response.error) {
          reject(new Error(response))
        } else {
          commit(types.USER_PRICING, response)
          resolve(response)
        }
      })
    })
  },

  // 유저 ID, PWD 로 토큰을 발급 받는다.
  getToken ({ commit }, userInfo) {
    return new Promise((resolve, reject) => {
      api.getToken(userInfo, response => {
        if (response.error) {
          reject(new Error(response.detail))
        } else {
          // 정상적으로 토큰이 발급 되었을 경우, 토큰 세팅.
          const token = response.token
          commit(types.SET_TOKEN, { token })
          resolve(response)
        }
      })
    })
  },

  // 가지고 있는 토큰에 만료시간을 체크하여 유효성을 검증한다.
  inspectToken ({ commit }, token) {
    return new Promise((resolve, reject) => {
      // 토큰이 있을 경우
      if (token) {
        // 디코딩 된 토큰 정보를 담는다.
        const decoded = decode(token)

        // 토큰 만료시간 (발급 후 2시간)
        const expiration = decoded.exp

        // 토큰 만료시간 + 1일 (만료후 갱신가능시간)
        const leeway = expiration + 86400

        // 토큰 발행시간 + 7일 (연속갱신 가능시간)
        const refresh = decoded.orig_iat + 604800

        // 현재 시간을 UTC로 환산
        const now = parseInt(moment.now() / 1000)

        // 토큰 갱신가능 시간, 연속 갱신 가능 시간을 체크한다.
        if (now < leeway && now < refresh) {
          if (now < expiration) {
            // 만료시간이 지나지 않았을 경우, 토큰 위변조 여부 검사.
            api.verifyToken(token, response => {
              if (response.error) {
                // 토큰 유효성 검증 실패 시, 로그아웃 처리.
                reject(new Error(response))
                commit(types.LOGOUT, 'invalid')
              } else {
                resolve(response)
              }
            })
          } else {
            // 만료시간이 지났을 경우, 토큰 갱신
            api.refreshToken(token, response => {
              if (response.error) {
                // 토큰 갱신 실패 시, 로그아웃 처리
                reject(new Error(response))
                commit(types.LOGOUT, 'invalid')
              } else {
                // 토큰 갱신 성공 시, 토큰 세팅
                const token = response.token
                commit(types.SET_TOKEN, { token })
                resolve(response)
              }
            })
          }
        } else {
          // 토큰이 유효하지 못 할 경우, 로그아웃 처리
          commit(types.LOGOUT, 'invalid')
        }
      }
    })
  },

  // 로그아웃
  logout ({ commit }) {
    return new Promise((resolve) => {
      commit(types.LOGOUT)
      resolve()
    })
  }
}

// mutations
const mutations = {
  // 유저 기본정보 설정
  [types.USER_INFO] (state, { user }) {
    state.user = user
    state.user.isLogin = true
  },

  [types.USER_PRICING] (state, response) {
    // 사용자정보에 유료정보를 업데이트 한다.
    state.user = Object.assign(state.user, response)
  },

  // 발급받은 토큰 처리
  [types.SET_TOKEN] (state, { token }) {
    // localStorage에 토큰 저장
    localStorage.setItem('_t', token)

    // Axios Request Header에 JWT Token 추가
    axios.defaults.headers.common['Authorization'] = 'JWT ' + token
  },

  // 로그아웃시 기본설정을 초기화 한다.
  [types.LOGOUT] (state, type = '') {
    // 유저정보 초기화
    state.user = {
      uid: null,
      lease_installer_id: null,
      name: 'unknown',
      username: 'unknown',
      email: '-',
      level: 0,
      isLogin: false,
      serviceList: [],
      mobileServiceList: []
    }

    // 수익조건 초기화
    state.condition = {}

    // 토큰 설정 초기화
    localStorage.removeItem('_t')
    delete axios.defaults.headers.common['Authorization']

    // 로그인 페이지로 이동
    window.Vue.$router.push('/login')
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/auth.js